import { Button, Group, Stack, Text } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { IngestionUploadsState } from '../../useIngestionUploads.types';
import { fadeRise } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import {
	DS_AI_COLORS,
	DS_AI_MOTION,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';

interface Props {
	uploads: IngestionUploadsState;
}

/** A large drop's progress, part by part, and each part the server refused, with Retry and Clear. */
export const UploadPartNotices = ({ uploads }: Props) => {
	const tCapManage = useTranslationCapManage();

	return (
		<>
			{uploads.progress !== null && (
				<Text
					component="p"
					size="md"
					m={0}
					c="dark.4"
					aria-live="polite"
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_PARTS_PROGRESS}
				>
					{tCapManage('dealstack_ai.ingest.upload.parts_progress', {
						part: uploads.progress.part,
						parts: uploads.progress.parts,
					})}
				</Text>
			)}
			{uploads.failedParts.map((failed) => (
				<Group
					key={failed.id}
					gap="sm"
					wrap="nowrap"
					py={12}
					px="sm"
					bd="1px solid red.3"
					bg="red.0"
					styles={{
						root: {
							borderRadius: 4,
							animation: `${fadeRise} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easeOut} both`,
						},
					}}
					role="alert"
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_FAILED_PART}
				>
					<Stack flex={1} gap={4} miw={0} fz="md" c="dark.4">
						<Text span size="md" fw={600} c={DS_AI_COLORS.errorText}>
							{tCapManage('dealstack_ai.ingest.upload.failed_part.title', {
								count: failed.count,
							})}
						</Text>
						<span>
							{failed.parts > 1
								? tCapManage(
										'dealstack_ai.ingest.upload.failed_part.message_part',
										{
											part: failed.part,
											parts: failed.parts,
										}
									)
								: tCapManage('dealstack_ai.ingest.upload.failed_part.message')}
						</span>
					</Stack>
					<Group gap="sm" wrap="nowrap">
						<Button
							size="height-36"
							variant="link-color"
							px={0}
							onClick={() => uploads.onClearPart(failed.id)}
							data-testid={
								TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_FAILED_PART_CLEAR_BUTTON
							}
						>
							{tCapManage('dealstack_ai.ingest.upload.failed_part.clear')}
						</Button>
						<Button
							size="height-36"
							fw={600}
							onClick={() => uploads.onRetryPart(failed.id)}
							data-testid={
								TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_FAILED_PART_RETRY_BUTTON
							}
						>
							{tCapManage('dealstack_ai.ingest.upload.failed_part.retry')}
						</Button>
					</Group>
				</Group>
			))}
		</>
	);
};
